import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles, Bot, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persistent session id per browser (localStorage)
const getSessionId = () => {
  let id = localStorage.getItem("apf_chat_session");
  if (!id) {
    id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("apf_chat_session", id);
  }
  return id;
};

const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Ciao! Sono Futuro Assistant. Sono qui 24/7 per aiutarti a scegliere il corso giusto. Chiedimi info su OSS, EIPASS, impianti, sicurezza sul lavoro o qualsiasi altro corso!"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const abortRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;

    setMessages((m) => [...m, { role: "user", text }, { role: "bot", text: "", streaming: true }]);
    setInput("");
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events line by line
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!rawEvent.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(rawEvent.slice(5).trim());
            if (payload.type === "delta" && payload.content) {
              full += payload.content;
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "bot" && last.streaming) {
                  copy[copy.length - 1] = { ...last, text: full };
                }
                return copy;
              });
            } else if (payload.type === "error") {
              throw new Error(payload.error || "Errore streaming");
            } else if (payload.type === "done") {
              // finalize
            }
          } catch (e) {
            // ignore malformed SSE lines
          }
        }
      }

      // Mark bot message as no longer streaming
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "bot" && last.streaming) {
          copy[copy.length - 1] = { ...last, streaming: false };
        }
        return copy;
      });
      if (!open) setHasNewMessage(true);
    } catch (err) {
      if (err.name === "AbortError") return;
      setMessages((m) => {
        const copy = [...m];
        // Replace the empty streaming bubble with an error message
        if (copy.length && copy[copy.length - 1].role === "bot" && copy[copy.length - 1].streaming) {
          copy[copy.length - 1] = {
            role: "bot",
            text: "Ops, qualcosa è andato storto. Riprova o chiamaci al +39 393 945 7831.",
            error: true,
          };
        } else {
          copy.push({
            role: "bot",
            text: "Ops, qualcosa è andato storto. Riprova o chiamaci al +39 393 945 7831.",
            error: true,
          });
        }
        return copy;
      });
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const quickReplies = [
    "Info corso OSS",
    "Certificazioni EIPASS",
    "Sicurezza 81/08",
    "Corsi in partenza"
  ];

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => { setOpen(true); setHasNewMessage(false); }}
        aria-label="Apri chat"
        className={`fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full bg-brand hover:bg-brand-dark text-white shadow-2xl flex items-center justify-center transition-all hover:scale-105 ${open ? "opacity-0 pointer-events-none scale-90" : "opacity-100 scale-100 animate-pulse-ring"}`}
      >
        <MessageCircle className="w-7 h-7" />
        {hasNewMessage && (
          <span className="absolute top-1 right-1 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-white"></span>
        )}
        <span className="absolute -top-1 -left-1 bg-yellow-400 text-brand-dark text-[10px] font-bold px-2 py-0.5 rounded-full shadow flex items-center gap-0.5">
          <Sparkles className="w-2.5 h-2.5" /> AI
        </span>
      </button>

      {/* Chat panel */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 max-w-md h-[600px] max-h-[calc(100vh-3rem)] bg-white rounded-3xl shadow-2xl border border-emerald-200 flex flex-col overflow-hidden transition-all duration-300 origin-bottom-right ${open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"}`}
      >
        {/* Header */}
        <div className="bg-brand text-white p-4 flex items-center gap-3">
          <div className="relative">
            <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <Bot className="w-6 h-6" />
            </div>
            <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-400 rounded-full border-2 border-brand"></span>
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-base leading-tight">Futuro Assistant</div>
            <div className="text-xs text-emerald-200 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></span> Online 24/7 · risposta immediata
            </div>
          </div>
          <button onClick={() => setOpen(false)} aria-label="Chiudi chat" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#fafaf7]">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "bot" && (
                <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center mr-2 flex-shrink-0 self-end">
                  <Bot className="w-4 h-4" />
                </div>
              )}
              <div
                className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                  m.role === "user"
                    ? "bg-brand text-white rounded-br-md"
                    : m.error
                    ? "bg-red-50 text-red-800 border border-red-200 rounded-bl-md"
                    : "bg-white text-brand-dark border border-emerald-100 rounded-bl-md"
                }`}
              >
                {m.text}
                {m.streaming && m.text && (
                  <span className="inline-block w-1.5 h-4 bg-brand ml-0.5 align-middle animate-pulse"></span>
                )}
              </div>
            </div>
          ))}

          {loading && messages.length > 0 && messages[messages.length - 1].streaming && !messages[messages.length - 1].text && (
            <div className="flex justify-start">
              <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center mr-2 flex-shrink-0 self-end">
                <Bot className="w-4 h-4" />
              </div>
              <div className="bg-white border border-emerald-100 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-brand rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                <span className="w-2 h-2 bg-brand rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                <span className="w-2 h-2 bg-brand rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
              </div>
            </div>
          )}
        </div>

        {/* Quick replies (only shown initially) */}
        {messages.length <= 1 && !loading && (
          <div className="px-4 pt-2 pb-1 flex flex-wrap gap-2 border-t border-emerald-100 bg-white">
            {quickReplies.map((q) => (
              <button
                key={q}
                onClick={() => send(q)}
                className="text-xs bg-brand-light hover:bg-emerald-200 text-brand px-3 py-1.5 rounded-full font-semibold transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="p-3 bg-white border-t border-emerald-100 flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            placeholder="Scrivi la tua domanda..."
            className="flex-1 h-11 px-4 rounded-full bg-[#fafaf7] border border-emerald-100 focus:outline-none focus:ring-2 focus:ring-brand text-sm text-brand-dark placeholder:text-brand-dark/40"
          />
          <button
            onClick={() => send()}
            disabled={loading || !input.trim()}
            aria-label="Invia messaggio"
            className="w-11 h-11 rounded-full bg-brand hover:bg-brand-dark disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors flex-shrink-0"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </>
  );
};

export default ChatBot;
